/**
 * **打牌那一屏的"桌面装置"** —— 热座和联机共用同一套版面。
 *
 * 评审这一轮把界面重画了一遍（原话在下面每条上），要求是**只留提到的**；
 * 下一轮又把剩下那些"说明性文字"也删了（见 §"没有提示"）：
 *
 * ```
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ 回合队列（左）                        [抽牌堆 N] ▤▤▤      │  ← 牌背一叠，数字在它左边
 *   │   PLAYER-1  8 张                      （弃牌堆 · 变暗）    │
 *   │  ▶PLAYER-2  8 张                                          │
 *   │   PLAYER-3  8 张                                          │
 *   │                                                          │
 *   │              （抽牌对峙时：中间那一扇可点的牌背）           │
 *   │                                                          │
 *   │                                    [结束回合]             │
 *   │                                     [⚙] ← 齿轮，右下角    │
 *   │  ────────────────── 手牌 ──────────────────                │
 *   └──────────────────────────────────────────────────────────┘
 * ```
 *
 * ## 哪些是评审点名的
 *
 * | 元素 | 出处 |
 * | --- | --- |
 * | 回合队列（左，左对齐、行动中最大最亮、依次递减、换回合向上滚一次） | 评审 |
 * | 抽牌堆 = 一叠牌背 + 左侧的数字 | 评审 |
 * | 弃牌堆变暗 | 评审 |
 * | 结束回合在设置左上 | 评审 |
 * | 设置 = 齿轮图标，点开是**菜单**（设置 / 保存并退出 / 退出对局） | 评审 |
 * | UI 盖在手牌层上方 | 评审 |
 * | **右下角那一个按钮**：结束回合 / 放弃（出局） | 评审（"结束回合按钮"） |
 *
 * ## ⚠️ 没有提示
 *
 * 评审：
 *
 * > 你把游戏 UI 的一堆按钮换成橙色字提示了，但是提示也不要有，完全没必要。
 * > 以及，UI 中遗落了大量完全没必要或已经过时的信息提示，删掉它们。
 *
 * 所以这一屏**一个字都不印**。删掉的是：
 *
 * - 桌面底部那一整块**状态行**（"轮到你了""抽一张 · 点一张牌背""打断链 2 层 · …"
 *   "PLAYER-1 获胜""把设备交给 PLAYER-2"）—— 它是上一版按钮的遗骸
 * - 弃牌堆旁边的 **`弃牌 N`**（抽牌堆张数评审点名要，这个没有）
 * - 齿轮菜单里那两句小字（"棋谱序列化还没做…""点面板外面 / 按 Esc 关掉"）
 *
 * ⚠️ **没有按钮列。** 评审：「你不应该在 UI 里放一大堆按钮，玩家想做什么直接
 * 对手牌进行操作就行了，不需要你搞一堆按钮。」所以界面不再把 `options()`
 * 铺成按钮 —— 它把那一串动作翻译成**点哪儿**（`tableIntent.ts` 那张表）：
 *
 * | 动作 | 怎么表达 |
 * | --- | --- |
 * | 出牌（单张/一对/三张/五张） | 选中手牌拖出去 |
 * | 需要指定对象 | 拖出去之后**点回合队列里的那个人** |
 * | 打断（休想） | 把那张牌拖出去 |
 * | 交一张（恩惠） | 把那张牌拖出去 |
 * | 抽一张 | 点牌背 |
 * | 把异常塞回牌堆 | 点抽牌堆（越靠上越接近牌堆顶） |
 * | 捞回弃牌堆一张 | 点弃牌堆里那张 |
 * | 结束回合 / 放弃出局 | 右下角**那一个**按钮（文案随局面变） |
 *
 * 一屏**最多一个**按钮（`MAX_BUTTONS`）—— 响应窗口删掉之后，
 * 连房主那个"过 / 立刻结算"的例外也没有了。
 *
 * ⚠️ 这个模块**不碰状态**：它只画，动作全走回调。
 */

import { Container, FillGradient, Graphics, Rectangle, type FederatedWheelEvent } from 'pixi.js';
import type { Renderer } from 'pixi.js';
import {
  INK,
  scrim,
  createColumn,
  richLabel,
  panel,
  stampButton,
  uiLine,
  uiPad,
} from '../../render/ui';
import { t, type TextKey, type TextParams } from '../../i18n';
import { createSurface } from '../../render/ui/state';
import { canHoverNow } from '../../render/input/current';
import { createAnimated, DURATION } from '../../render/ui/motion';
import { attachPan, type Pan } from '../../render/ui/pan';
import { stampInkAt } from '../../render/ui/ink';
import { HOLD_CONFIRM_MS, HOLD_HINT_MS } from '../../render/ui/hold';
import { DrawPile } from '../../render/game/drawPile';
import { faceSize } from '../../render/card/metrics';
import { cardAccent } from '../../render/card';
import { TrackerHeader } from '../../render/game/trackerHeader';
import {
  buildSpanCount,
  bindSpanCell,
  buildStateIcons,
  buildStateLegend,
  spanIconSet,
  type StateCounts,
} from '../../render/game/spanIcon';
import { toLocalBox, type HoverSource } from '../../render/game/cardTooltip';
import {
  drawPileFaces,
  statusCountsOf,
  trackDeck,
  type TrackerRow,
} from '../../analysis/tracker';
import { TurnQueue } from '../../render/game/turnQueue';
import { SpectatorFan } from '../../render/game/spectatorFan';
import { InsertHint } from '../../render/game/insertHint';
import { TargetEdgeEffect } from '../../render/game/targetEdge';
import { insertOutcome } from '../../analysis/insertInfo';
import type { CardDef } from '../../core/card';
import { cardName } from '../../core/cardText';
import type { CardTitle, CardUid, PlayerId } from '../../core/ids';
import type { GameAction } from '../../core/actions';
import type { PlayerView } from '../../core/project';
import type { TableIntent } from './tableIntent';
import type { ScreenContext } from '../screen';

// ---------------------------------------------------------------- 版面 ----

/** 回合队列：左上。⚠️ 左对齐是评审点名的，所以只给 x 和宽度 */
const QUEUE_X = 28;
const QUEUE_Y = 40;
const QUEUE_W = 260;

/** 抽牌堆 / 弃牌堆：右上 */
const RIGHT_MARGIN = 28;
/** 抽牌堆飞入中心并展开 / 记牌器窗口与背景进入的动画时长（毫秒） */
const EXPAND_MS = 320;
const DRAW_Y = 34;
const DRAW_BACK_HEIGHT = 96;

/** 齿轮与结束回合：右下 */
const GEAR_SIZE = 46;
const GEAR_MARGIN = 28;
const END_TURN_W = 176;
const END_TURN_H = 40;

/** 抽牌对峙那一扇牌背的落点（桌面正中） */
const FAN_CENTER = { x: 620, y: 386 };

/** 齿轮菜单的宽度 */
const MENU_W = 220;

export interface TableChromeOptions {
  readonly ctx: ScreenContext;
  readonly renderer: Renderer;
  /** 牌名 → 定义（弃牌堆要真牌面） */
  readonly defOf: (title: CardTitle) => CardDef | undefined;
  /** 结束回合。找不到那个合法动作时按钮自己禁用 */
  readonly onEndTurn: () => void;
  /** 齿轮菜单 → 设置 */
  readonly onSettings: () => void;
  /** 齿轮菜单 → 刷新同步（联机才有；热座不传，菜单里就不出现这一项） */
  readonly onSync?: () => Promise<void> | void;
  /** 齿轮菜单 → 退出对局 */
  readonly onLeave: () => void;
  /** 点了一条合法动作（右下角那个按钮、点牌堆、点弃牌堆都汇到这里） */
  readonly onAction: (action: GameAction) => void;
  /** 点了牌背（抽牌对峙） */
  readonly onPickBack: (slot: number) => void;
  /** hover 了某张牌背（联机要发给对面）。热座不传 */
  readonly onFanHover?: (slot: number | null) => void;
  /** ★ 点了回合队列里的某个玩家（= 给这一手指定对象） */
  readonly onPickTarget?: (player: PlayerId) => void;
}

/**
 * **要玩家手动选的一手**：候选目标 +（三张同名时）候选牌名。
 *
 * `titles === null` = 只选目标；给了就是「目标 + 牌名」合并面板。
 * `onPick` 里 `title === null` 表示只选了目标（单张 / 一对）。
 */
export interface PlayChoiceSpec {
  readonly targets: readonly { readonly id: PlayerId; readonly name: string }[];
  readonly titles: readonly CardTitle[] | null;
  readonly onPick: (target: PlayerId, title: CardTitle | null) => void;
}

export interface TableChrome {
  /** 加进容器。⚠️ 要加在**手牌之后** —— UI 盖在手牌层上方（评审要求） */
  readonly container: Container;
  /**
   * **齿轮菜单那一层**：全屏遮罩 + 居中面板。
   *
   * ⚠️ 调用方要把它加在**所有东西之后**（手牌、装置、翻牌特效都算）——
   * 评审要求"按下设置按钮后弹出的菜单盖在整个屏幕之上"。
   */
  readonly overlay: Container;
  /**
   * **牌扇层**（取牌扇）。`container` 在翻牌特效之下会挡住它，所以单独一层 ——
   * 调用方要把它加在 `effects` **之上**。
   */
  readonly fanLayer: Container;
  /** 菜单开着吗（Esc 先关它，再退屏） */
  menuOpen(): boolean;
  closeMenu(): void;
  /**
   * 视图变了就调它。
   *
   * `intent` 是"现在该让玩家点哪儿"（`tableIntent.ts` 算好的纯数据）——
   * 界面不自己读 `options()`，因为那正是"铺一堆按钮"的老路。
   */
  update(view: PlayerView, intent: TableIntent): void;
  /** 每帧（队列滚动 + 牌背缓动）。`deltaMS` 来自那唯一一个 ticker */
  updateFrame(deltaMS: number): void;
  /**
   * **目标高亮**：行动者已经选定的目标（`null` = 没有）。
   *
   * 被选中的名字对应的屏幕边缘光要一直亮到本次行动结束；hover 的那一个
   * 由 `TurnQueue` 自己的回调驱动（见 `createTableChrome` 里那个 `let`）。
   */
  setTargetHighlight(target: PlayerId | null): void;
  /** 桌面正中。翻牌动画摆这儿 */
  center(): { readonly x: number; readonly y: number };
  /**
   * **抽牌堆最上面那张牌背的中心**（桌面坐标系）。
   *
   * 抽牌动画从这里起飞 —— 评审：「按回合结束时，在抽牌堆顶部位置生成一张牌背」。
   * 那个位置只有这一层知道（它是抽牌堆的排版），所以由这里交出去，
   * 而不是让动画那边按 `DRAW_Y` 之类的常数自己算一遍。
   */
  drawTop(): { readonly x: number; readonly y: number };
  /**
   * **出牌处** —— 出牌动画从这里起飞（牌心）。
   *
   * 打出的牌在手里就看得见，所以起飞点取**手牌那一带的中间偏上**。
   * 位置只有这一层知道（它是版面），由这里交出去，不让动画自己拿常数估。
   */
  playOrigin(): { readonly x: number; readonly y: number };
  /** 手牌层从哪一行开始让位（逻辑像素） */
  readonly handTop: number;
  /** 现在那一扇可点的牌背（没有就是 `null`） */
  fan(): SpectatorFan | null;
  /** 记牌器面板开着吗（Esc 先关它） */
  trackerOpen(): boolean;
  closeTracker(): void;
  /** 手柄/程序化：结束回合（和右下角那个按钮同一件事） */
  endTurn(): void;
  /** 手柄/程序化：开关记牌器 */
  toggleTracker(): void;
  /** 记牌器表头现在指着哪张牌（给屏幕的 tooltip 用）。没有就是 null */
  trackerHover(): HoverSource | null;
  /**
   * **记牌器里的数字格现在指着哪一个**（给屏幕的 tooltip 用）。没有就是 null。
   *
   * 数字格用图标表示（≤4 个整牌图标 / ×N / 小数进度条），读不出精确的小数；
   * 指上去（触控：点一下）用说明面板把精确值讲清楚。
   */
  trackerCellTip(): {
    readonly title: string;
    readonly tip: TextKey;
    readonly tipParams: TextParams;
    readonly box: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  } | null;
  /**
   * **手动选目标 / 牌名。**候选都铺在画面中央：
   *
   * - 单张 / 一对：列出候选玩家名，点一个就走；
   * - 三张同名：候选玩家名 + 候选牌类**同时**铺出来（规则允许命名手里没有
   *   的牌名，而"拖一张手里的牌"表达不了那种选择，见审计 M2）。
   */
  choosePlay(spec: PlayChoiceSpec): void;
  /** 选择面板开着吗（Esc 先关它） */
  chooserOpen(): boolean;
  closeChooser(): void;
  /**
   * **右下角那个按钮的现场读数**（冒烟用）。
   *
   * 评审：「在濒死期间，我希望按钮变红，且需要长按确认（长按期间有一个按钮上
   * 要有一个从左到右的条）。」——"变红"和"条子涨了多少"都是**画出来的**，
   * 所以这里把按钮自己的状态交出来（`confirm` / `hold` / `label`），
   * 冒烟就能在真浏览器里断言，而不是靠截图数像素。
   */
  buttons(): {
    readonly finish: {
      readonly label: string;
      readonly enabled: boolean;
      readonly confirm: boolean;
      readonly hold: number;
      /** 触控按住够久、正在看这个按钮的说明吗（长按代替 hover） */
      readonly hinting: boolean;
      /** 被攻击的第一个额外回合：结束键用的来源牌颜色（没被攻击 = null） */
      readonly accent: number | null;
    };
  };
  /**
   * **hover 到了动作按钮吗、它是哪条说明、摆在哪儿。**
   *
   * 返回 null = 不显示（没 hover / 没启用 / 这一阶段没有说明）。
   * 坐标是**逻辑画布坐标**（按钮直接挂在 0,0 的 chrome 层上）。
   */
  actionTip(): {
    /** 按钮上的字，当说明面板的标题 */
    readonly title: string;
    readonly tip: TextKey;
    readonly tipParams?: TextParams;
    readonly box: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  } | null;
  destroy(): void;
}

export function createTableChrome(options: TableChromeOptions): TableChrome {
  const { ctx, renderer } = options;
  const container = new Container();
  container.label = 'table-chrome';

  /** 指针现在停在哪个可点的名字上（目标高亮用）。来自 TurnQueue 的 hover 回调 */
  let hoverTarget: PlayerId | null = null;
  /** 行动者已经**选定**的目标 —— 它要一直亮到本次行动结束 */
  let pinnedTarget: PlayerId | null = null;

  const queue = new TurnQueue({
    scale: ctx.settings.get().uiScale,
    width: QUEUE_W,
    ...(options.onPickTarget === undefined ? {} : { onPickTarget: options.onPickTarget }),
    onHoverTarget: (id) => {
      hoverTarget = id;
    },
  });
  container.addChild(queue.view);

  const draw = new DrawPile({
    renderer,
    height: DRAW_BACK_HEIGHT,
    width: ctx.bounds.width,
    defOf: options.defOf,
    faces: ctx.faces,
    input: ctx.input,
  });
  const right = new Container();
  const drawCount = richLabel({ text: t('game.cardCount', { n: 0 }), scale: 1, size: 16, fill: INK.text, role: 'cjk' });
  right.addChild(draw.view, drawCount);
  container.addChild(right);

  /**
   * **右下角那一个按钮。**
   *
   * 文案随局面变：出牌阶段是"结束回合"（= 抽一张）、濒死时是"放弃（出局）"。
   * 它一次只做一件事 —— 就是当前阶段**唯一的推进动作**。
   */
  const finish = stampButton({
    label: t('common.endTurn'),
    scale: ctx.settings.get().uiScale,
    variant: 'primary',
    width: END_TURN_W,
    height: END_TURN_H,
    // ★ 濒死时它会变红并要求长按（见下面 `setConfirm`）—— 那时候它是「放弃」
    holdMs: HOLD_CONFIRM_MS,
    // ★ 触控上按住 350ms 看 tip（没有 hover）；看过说明的那一下不再触发结束回合
    hintHoldMs: HOLD_HINT_MS,
    onPress: () => endTurn(),
  });
  finish.setEnabled(false);
  container.addChild(finish.container);

  /** 记牌器的开关与面板。数据来自 `analysis/tracker`（纯函数，可单测） */
  let trackerOpen = false;
  let tracker: Container | null = null;
  let lastView: PlayerView | null = null;

  const menu = createGearMenu({
    scale: ctx.settings.get().uiScale,
    canvas: ctx.bounds,
    onSettings: options.onSettings,
    ...(options.onSync === undefined ? {} : { onSync: options.onSync }),
    onLeave: options.onLeave,
  });
  // ⚠️ 按钮在桌面上，**菜单那一层不在** —— 它要盖住整个屏幕（包括手牌和特效），
  //    所以由调用方加在最后一层，见 `TableChrome.overlay`
  container.addChild(menu.button);

  /**
   * **盖在最上面那一层**：齿轮菜单 + 对局结算页。
   * 调用方把它加在最后（见 `TableChrome.overlay` 的约定）。
   */
  const overlay = new Container();
  overlay.label = 'table-overlay';
  // ★ 目标高亮的屏幕边缘光 + 方块粒子。加在 overlay 的**最底下** ——
  //   它是一圈屏幕边缘，不该盖住桌面上的牌和控件。
  const targetEdge = new TargetEdgeEffect({
    scale: ctx.settings.get().uiScale,
    bounds: ctx.bounds,
  });
  overlay.addChild(targetEdge.container);
  // 屏幕中央的轻提示（"对手让你交出一张牌"）——给牌的人得知道现在要干什么
  const promptText = richLabel({
    text: '',
    scale: ctx.settings.get().uiScale,
    size: 16,
    fill: INK.text,
    role: 'cjk',
    bold: true,
  });
  const prompt = new Container();
  prompt.label = 'table-center-prompt';
  prompt.visible = false;
  prompt.addChild(promptText);
  overlay.addChild(prompt);
  overlay.addChild(menu.overlay);
  // ★ 抽牌堆（和它旁边的牌数）提到 overlay 层：展开成横排时必须在手牌之上；
  //   记牌器面板也放这一层，且排在抽牌堆**下面**（见 `rebuildTracker`）。
  overlay.addChild(draw.view, drawCount);

  // ★ 塞回异常时贴在**被拖那张牌正下方**的功能提示（第几张被抽到 / 谁抽到）。
  //   和 draw.view 同在 overlay 层，所以 insertBox() 给的坐标直接可用。
  const insertHint = new InsertHint({
    scale: ctx.settings.get().uiScale,
    bounds: { width: ctx.bounds.width, height: ctx.bounds.height },
  });
  overlay.addChild(insertHint.container);

  function closeTracker(): void {
    tracker?.destroy({ children: true });
    tracker = null;
    trackerOpen = false;
    trackerHeadHover = null;
    trackerCellHover = null;
    // ★ 清掉表头引用：表头容器刚随 tracker 一起 destroy（它的 position 变成 null），
    //   不清的话下一帧 updateFrame 还会去写 head.view.y，直接崩。
    trackerHeads.length = 0;
    // 抽牌堆收回右上角（有动画）
    draw.setExpanded(false);
    layout();
  }

  /**
   * **重画记牌器面板。** 打开期间每次 `update()` 都重画一遍（视图变了要跟上）。
   *
   * 内容分两段：牌堆顶**确定的**那几张；以及每种牌"还没露面"的张数与
   * 期望分布（`trackDeck` 算的）。
   */
  function rebuildTracker(view: PlayerView): void {
    if (!trackerOpen) return;
    tracker?.destroy({ children: true });
    const { width, height } = ctx.bounds;
    const box = new Container();
    box.label = 'tracker';
    box.addChild(scrim({ scale, width, height, onDismiss: closeTracker }).container);

    // ★ 只占**屏幕下半部分**：一张横向表
    const MARGIN = uiPad(16, scale);
    const W = width - MARGIN * 2;
    const H = height / 2 - MARGIN - MARGIN / 2;
    // 有"从弃牌堆捞一张"待决时，标题提示可以点表头
    const card = panel({
      scale,
      width: W,
      height: H,
      title: pickMap.size > 0 ? t('game.tracker.titlePick') : t('game.tracker.title'),
    });
    const body = card.body;
    const pad = uiPad(12, scale);
    const mem = trackDeck(view, ctx.registry);
    const rows = mem.rows;
    const n = rows.length;

    const labelW = uiPad(92, scale);
    const rowH = uiPad(26, scale);
    const minColW = uiPad(46, scale);
    const colPad = uiPad(12, scale);

    /**
     * **纵向 = 几种去向**：合并状态（已打出 / 我手里 / 未知，三色图标，加起来 = 总数）
     * / 牌堆（定位/区域~/未~），然后**每个其他玩家各占一行**。
     */
    /** 单元格的值：单个数 / 一组数（"定/区~/未~"）/ 合并状态（三个计数） */
    type CellValue = number | readonly number[] | StateCounts;
    const isState = (value: CellValue): value is StateCounts => typeof value === 'object' && !Array.isArray(value);
    const metrics: readonly (readonly [string, (row: TrackerRow) => CellValue])[] = [
      [
        t('game.tracker.row.status'),
        // ★ 「未知」= 总数 − 弃牌堆 − 我手里（statusCountsOf）。不再用记牌器的
        //   belief —— 那样"已打出 / 我手里 / 未知"三色加起来对不上在场总数。
        (row): StateCounts => statusCountsOf(row),
      ],
      [t('game.tracker.row.located'), (row) => row.locatedInDraw],
      [t('game.tracker.row.region'), (row) => row.regionInDraw],
      [
        t('game.tracker.row.untracked'),
        (row) => Math.max(0, row.expectedInDraw - row.locatedInDraw - row.regionInDraw),
      ],
      ...mem.otherPlayers.map(
        (player) =>
          [
            t('game.tracker.row.otherHand', { name: player.name }),
            (row: TrackerRow) => row.expectedInHands[player.id] ?? 0,
          ] as const,
      ),
    ];

    // ① 先量单元格宽度；表头改成**一张小牌面**（不再是牌名文字）
    const headSize = faceSize(uiPad(34, scale));
    /** 单元格：≤4 画对应数量的 span icon，>4 画 "icon × N"，小数画半张 */
    const iconH = spanIconSet(renderer, scale).height;
    const buildCell = (value: CellValue, wrapW: number): Container => {
      if (isState(value)) return buildStateIcons(renderer, value, scale, wrapW);
      if (typeof value === 'number') return buildSpanCount(renderer, value, scale);
      const group = new Container();
      let gx = 0;
      for (let i = 0; i < value.length; i++) {
        const part = value[i];
        if (part === undefined) continue;
        if (i > 0) {
          const sep = richLabel({ text: '/', scale, size: 11, fill: INK.textDim, role: 'mono' });
          sep.position.set(gx, (iconH - sep.height) / 2);
          group.addChild(sep);
          gx += sep.width + uiPad(2, scale);
        }
        const span = buildSpanCount(renderer, part, scale);
        span.position.x = gx;
        group.addChild(span);
        gx += span.width + uiPad(2, scale);
      }
      return group;
    };
    // 状态行按 minColW 预折行，先不参与列宽竞争
    const provisionalWrap = Math.max(1, minColW - colPad);
    const baseCells = metrics.map(([, valueOf]) => rows.map((row) => buildCell(valueOf(row), provisionalWrap)));
    const colW = rows.map((_row, i) => {
      let widest = 0;
      for (const line of baseCells) widest = Math.max(widest, line[i]?.width ?? 0);
      return Math.max(minColW, headSize.width + colPad, widest + colPad);
    });
    // 再用真实列宽重建状态行（按列折行）
    const cells = metrics.map(([, valueOf], m) =>
      rows.map((row, i) => {
        const value = valueOf(row);
        return isState(value)
          ? buildCell(value, Math.max(1, (colW[i] ?? minColW) - colPad))
          : (baseCells[m]?.[i] ?? buildCell(value, provisionalWrap));
      }),
    );
    const gridW = colW.reduce((sum, width) => sum + width, 0);

    // ② 表头 = 每种牌一张**小牌面**（有 idle、hover 出 tooltip）+ 单元格
    const grid = new Container();
    grid.label = 'tracker-grid';
    trackerHeads.length = 0;
    trackerHeadHover = null;
    // ⚠️ 重画会把数字格销毁：留着旧的引用，下一帧 trackerCellTip() 会在
    //    已销毁的容器上 getBounds() 直接崩。
    trackerCellHover = null;
    // ★ 三色图例（贴在表盘顶部，网格整体下移让开）。
    //   提示按当前输入挑：能 hover 说「悬停」，触控说「点按」，走焦不提示。
    const traits = ctx.traits();
    const legendHint =
      traits.keyboardActive || traits.gamepadActive
        ? null
        : t(traits.canHover ? 'game.tracker.hintHover' : 'game.tracker.hintTouch');
    const legend = buildStateLegend(renderer, scale, legendHint);
    legend.position.set(uiPad(12, scale), uiPad(10, scale));
    body.addChild(legend);
    const gridTop = legend.y + legend.height + uiPad(8, scale);
    // ★ 记牌器取代弃牌堆：要"从弃牌堆捞一张"时，对应牌型的表头可点（同名牌任取一张即可）
    const pickTitleAction = new Map<CardTitle, GameAction>();
    for (const discardCard of view.discardPile) {
      const action = pickMap.get(discardCard.uid);
      if (action !== undefined && !pickTitleAction.has(discardCard.title)) {
        pickTitleAction.set(discardCard.title, action);
      }
    }
    /** 触控横滚的手势（表头 onPick 要靠它挡掉拖动后的余波 tap） */
    let trackerPan: Pan | null = null;
    let x = 0;
    for (let i = 0; i < n; i++) {
      const row = rows[i];
      const def = row === undefined ? undefined : ctx.registry.get(row.title);
      if (def !== undefined) {
        const pickAction = row === undefined ? undefined : pickTitleAction.get(row.title);
        const header = new TrackerHeader({
          faces: ctx.faces,
          def,
          width: headSize.width,
          height: headSize.height,
          onHover: (hoveredDef) => {
            if (hoveredDef !== null) {
              trackerHeadHover = { def: hoveredDef, view: header.view };
            } else if (trackerHeadHover?.view === header.view) {
              trackerHeadHover = null;
            }
          },
          // ★ 点表头：能捞牌就捞牌；否则（触控）切换它的说明面板
          onPick: () => {
            // 触控横滚刚把这一行拖过来时不要顺手把牌捞走 / 弹说明
            if (trackerPan?.justDragged() === true) return;
            if (pickAction !== undefined) {
              options.onAction(pickAction);
              return;
            }
            if (canHoverNow()) return;
            trackerHeadHover = trackerHeadHover?.view === header.view ? null : { def, view: header.view };
          },
        });
        header.view.position.set(x + (colW[i] ?? 0) / 2 - headSize.width / 2, gridTop);
        grid.addChild(header.view);
        trackerHeads.push(header);
      }
      x += colW[i] ?? 0;
    }

    /**
     * **数字格挂上 hover / 点按。** hover 出精确小数；触控改成点按开关。
     *
     * ⚠️ 指针事件在组件层（`bindSpanCell`）—— 屏幕层不许自己挂
     *    （`test/ui/design.test.ts`）。这里只管「指到哪一个」和「点开后弹什么」。
     * ⚠️ 只挂在**数字**格上 —— 状态格（三个计数）本来就是整数，没有更精确的小数。
     */
    function bindCell(cell: Container, title: string, value: number): void {
      bindSpanCell(cell, {
        enter: () => {
          trackerCellHover = { view: cell, title, value };
        },
        leave: () => {
          if (trackerCellHover?.view === cell) trackerCellHover = null;
        },
        toggle: () => {
          // 触控横滚刚把这一格拖过来时不要弹说明
          if (trackerPan?.justDragged() === true) return;
          trackerCellHover = trackerCellHover?.view === cell ? null : { view: cell, title, value };
        },
      });
    }
    let y = gridTop + headSize.height + uiPad(10, scale);
    for (let m = 0; m < metrics.length; m++) {
      const name = metrics[m]?.[0] ?? '';
      const rowCells = cells[m] ?? [];
      // 状态行折行后可能比一行高 —— 这一行按最高的格子算高度
      let rowHeight = rowH;
      for (const cell of rowCells) {
        if (cell !== undefined) rowHeight = Math.max(rowHeight, cell.height + uiPad(4, scale));
      }
      const rowLabel = richLabel({ text: name, scale, size: 11, fill: INK.textDim, role: 'cjk' });
      rowLabel.position.set(0, y + (rowHeight - rowLabel.height) / 2);
      body.addChild(rowLabel);
      let cx = 0;
      for (let i = 0; i < n; i++) {
        const cell = rowCells[i];
        if (cell !== undefined) {
          cell.position.set(cx + (colW[i] ?? 0) / 2 - cell.width / 2, y + (rowHeight - cell.height) / 2);
          const cardRow = rows[i];
          const raw = cardRow === undefined ? undefined : metrics[m]?.[1](cardRow);
          // 只有数字格有「更精确的小数」可讲
          if (typeof raw === 'number') bindCell(cell, name, raw);
          grid.addChild(cell);
        }
        cx += colW[i] ?? 0;
      }
      grid.addChild(
        new Graphics()
          .rect(0, y + rowHeight, Math.max(gridW, 1), uiLine(1, scale))
          .fill({ color: INK.edge, alpha: 0.4 }),
      );
      y += rowHeight;
    }

    // ③ 网格套遮罩 + 滚轮横滚（列宽不被挤压；总宽超出视口时滚动）
    const viewportW = W - pad * 2;
    const gridViewportW = Math.max(1, viewportW - labelW);
    const gridMask = new Graphics().rect(labelW, 0, gridViewportW, H).fill({ color: 0xffffff });
    body.addChild(gridMask);
    grid.mask = gridMask;
    grid.position.set(labelW, 0);
    body.addChild(grid);
    const maxScroll = Math.max(0, gridW - gridViewportW);
    // ⚠️ 网格原点在 `labelW`（行名列的右边），所以「回到最左」是 x = labelW，
    //    不是 0。原来把上界写成 0，一滚就会把网格左移一整条行名列的宽度。
    const restX = labelW;
    const minX = restX - maxScroll;
    const clampX = (value: number): number => (value < minX ? minX : value > restX ? restX : value);
    body.eventMode = 'static';
    body.hitArea = new Rectangle(0, 0, viewportW, H);
    body.on('wheel', (event: FederatedWheelEvent) => {
      event.preventDefault?.();
      grid.x = clampX(grid.x - event.deltaY);
    });
    // 触控横滚：和滚轮同一根轴、同一套边界与回弹（见 `ui/pan.ts`）
    trackerPan = attachPan({
      surface: body,
      axis: 'x',
      scale,
      bounds: () => ({ min: minX, max: restX }),
      read: () => grid.x,
      write: (value) => {
        grid.x = value;
      },
    });

    const close = stampButton({
      label: t('common.close'),
      scale,
      variant: 'primary',
      width: uiPad(140, scale),
      height: uiPad(32, scale),
      onPress: closeTracker,
    });
    close.container.position.set((viewportW - close.container.width) / 2, y + uiPad(8, scale));
    body.addChild(close.container);

    card.container.position.set(
      Math.round((width - W) / 2),
      Math.round(height / 2 + MARGIN / 2),
    );
    box.addChild(card.container);
    // ★ 进入动画：窗口 + 背景一起淡入，进度和抽牌堆飞行同一条时间线
    box.alpha = trackerEnter;
    tracker = box;
    // ★ 都在 overlay 层：记牌器在**下**、抽牌堆在上、齿轮菜单最上
    overlay.addChild(box);
    overlay.addChild(draw.view, drawCount);
    overlay.addChild(menu.overlay);
  }

  let scale = ctx.settings.get().uiScale;
  /** 抽牌堆最上面那张牌背的中心。`layout()` 每帧算一次，`drawTop()` 读它 */
  let drawTop = { x: 0, y: 0 };
  /** 抽牌堆的当前位置：收起=右上角，展开=中心上方。位置动画按 `EXPAND_MS` 走 */
  let drawPos = { x: 0, y: 0 };
  let drawAnimFrom = { x: 0, y: 0 };
  let drawAnimElapsed = EXPAND_MS;
  let drawLastTarget: { x: number; y: number } | null = null;
  let drawInit = false;
  /** 记牌器窗口 / 背景的进入动画进度 0..1（和抽牌堆飞行同长） */
  let trackerEnter = 1;
  /** 记牌器表头的小牌（有 idle；hover 出 tooltip） */
  const trackerHeads: TrackerHeader[] = [];
  let trackerHeadHover: { def: CardDef; view: Container } | null = null;
  /** 记牌器数字格现在指着哪一个（hover / 点按）—— 精确小数说明靠它 */
  let trackerCellHover: { view: Container; title: string; value: number } | null = null;
  /** A2 选牌 overlay 里的牌节点（也要每帧推 idle/hover） */
  let chooserHeaders: TrackerHeader[] = [];
  /** ★ **常驻**的取牌扇：节点一直在，不抽牌时渲染为空 */
  const pickFan = new SpectatorFan({
    renderer,
    registry: ctx.registry,
    faces: ctx.faces,
    height: 108,
    maxWidth: uiPad(520, scale),
  });
  // 绕牌面的**竖直中心**倒挂；位置在 `layout()` 里每帧给
  pickFan.view.pivot.set(0, pickFan.size.height / 2);
  pickFan.view.rotation = Math.PI;
  // ★ 单独一层（调用方加在 effects 之上）——取牌扇本来就在桌面正中，
  //   压在翻牌/出牌特效下面会被挡得看不见。
  const fanLayer = new Container();
  fanLayer.label = 'table-fan-layer';
  // ★ 取牌时给牌扇垫一层暗底 + 一句提示（牌扇本身是倒挂的，文字保持正向）
  const fanShade = new Graphics();
  fanShade.label = 'steal-shade';
  // ★ 柔边：径向渐变（中间最暗、四周透明），不是一块硬边矩形
  const fanGradient = new FillGradient({
    type: 'radial',
    center: { x: 0.5, y: 0.5 },
    innerRadius: 0,
    outerRadius: 0.5,
    colorStops: [
      { offset: 0, color: 'rgba(0,0,0,0.72)' },
      { offset: 0.45, color: 'rgba(0,0,0,0.55)' },
      { offset: 0.75, color: 'rgba(0,0,0,0.18)' },
      { offset: 1, color: 'rgba(0,0,0,0)' },
    ],
    textureSpace: 'local',
  });
  const fanHint = richLabel({ text: t('game.steal.hint'), scale: 1, size: 20, fill: INK.text, role: 'cjk' });
  fanHint.label = 'steal-hint';
  fanLayer.addChild(fanShade, fanHint, pickFan.view);
  let fanActive = false;
  let fanCount = -1;
  /** 取牌暗底 / 提示的不透明度 0..1（目标是 fanActive，收尾时急停渐隐到 0） */
  const fanChrome = createAnimated(0);
  /** 右下角那个按钮现在绑着什么（`intent` 每帧给） */
  let finishAction: TableIntent['finish'] = null;
  /** 那个按钮的 hover 说明键（`intent` 每帧给；null = 不显示） */
  let finishTip: TextKey | null = null;
  /** 说明里的 `param` 值（牌名 / 回合数…） */
  let finishTipParams: TextParams | undefined;
  /** 抽牌堆现在是"选插入位置"的模式吗（非空就是） */
  let insertSpots: TableIntent['insert'] = [];
  /** 弃牌堆现在是"捞一张"的模式吗（非空就是） */
  let pickMap: ReadonlyMap<CardUid, GameAction> = new Map();

  /**
   * 摆一遍**固定**的那些件（`update` 每次都调它）。
   *
   * ⚠️ 这些件是**跨帧活着**的（队列在滚动、牌背在缓动），所以不是每次视图
   * 变化推倒重来 —— 位置由这里统一重算一遍就够了。
   */
  /** 牌数标签跟着抽牌堆走：收起时在左边，展开时在下面居中 */
  function updateDrawCount(): void {
    if (trackerOpen) {
      drawCount.position.set(
        ctx.bounds.width / 2 - drawCount.width / 2,
        drawPos.y + draw.size.height / 2 + uiPad(8, scale),
      );
      return;
    }
    drawCount.position.set(
      drawPos.x - draw.contentWidth / 2 - uiPad(10, scale) - drawCount.width,
      drawPos.y - drawCount.height / 2,
    );
  }

  /**
   * **取牌扇那层**：位置 + 暗底 / 提示的几何。`layout()` 和 `syncFan()` 都要调它。
   *
   * ⚠️ 几何**常画**、显隐只看 `applyFanChromeAlpha()`（由 `fanChrome` 进度决定）——
   * 不再按 `fanActive` 硬开关。这一条同时修掉了"行动完成后暗底 / 提示滞留不消失"：
   * 旧代码里 `layout()` 跑在 `syncFan()` 之前，`syncFan` 把 `fanActive` 翻成 false
   * 后直接 `return`，没有任何重绘，于是要等下一次视图变化才可能收掉。
   * 现在收尾由 `updateFrame` 每帧推进 alpha，和视图变化解耦。
   */
  function paintFanChrome(): void {
    const s = scale;
    const fanX = uiPad(FAN_CENTER.x, s);
    const fanY = uiPad(FAN_CENTER.y, s);
    pickFan.view.position.set(fanX, fanY);
    // ⚠️ 几何**常画**，显隐交给 alpha：暗底 / 提示要在收尾那几百毫秒里
    //    一边变透明一边还在（见 applyFanChromeAlpha）。按 fanActive 硬开关
    //    的话，行动一完成它们就“啪”地没了（用户报的）。
    fanShade.clear();
    const bandW = uiPad(620, s);
    const bandH = uiPad(180, s);
    fanShade.ellipse(fanX, fanY, bandW / 2, bandH / 2).fill(fanGradient);
    fanHint.position.set(Math.round(fanX - fanHint.width / 2), Math.round(fanY + uiPad(72, s)));
    applyFanChromeAlpha();
  }

  /**
   * **把暗底 / 提示刷成当前不透明度。**
   *
   * fanChrome 是 0..1 的进度：fanActive 是目标，updateFrame 每帧把它缓过去。
   * 这样行动完成后是自然渐隐，而不是整块消失。
   */
  function applyFanChromeAlpha(): void {
    const a = fanChrome.value;
    const on = a > 0.002;
    fanShade.visible = on;
    fanHint.visible = on;
    fanShade.alpha = a;
    fanHint.alpha = a;
  }

  function layout(): void {
    const s = scale;
    const { width, height } = ctx.bounds;
    // ★ 安全区：边缘 HUD 往内让。刘海/圆角在侧边、手势条在底边
    const safe = ctx.layout().safe;
    const rightX = uiPad(width - RIGHT_MARGIN, s) - safe.right;

    // ⚠️ 往下让出"行动中那一行上面那几行"的高度，否则整排"之前的人"在画布外
    queue.view.position.set(
      uiPad(QUEUE_X, s) + safe.left,
      uiPad(QUEUE_Y, s) + safe.top + queue.anchorOffset,
    );

    // 齿轮在最右下；结束回合在它的**左上**（评审要求）
    const gearX = rightX - uiPad(GEAR_SIZE, s);
    const gearY = uiPad(height - GEAR_MARGIN, s) - uiPad(GEAR_SIZE, s) - safe.bottom;
    menu.button.position.set(gearX, gearY);
    menu.layout(s);
    finish.container.position.set(
      gearX - uiPad(12, s) - uiPad(END_TURN_W, s),
      gearY - uiPad(END_TURN_H, s) - uiPad(10, s),
    );

    paintFanChrome();
  }

  // ---- 结算页（对局结束时盖在最上面）----
  //
  // 评审：对局结束需要一个本局结算页，展示玩家的胜负和一些别的信息。
  // 热座与联机共用这一份：只要视图 `phase === 'finished'` 就盖上。
  let settlement: Container | null = null;
  let settlementBuilt = false;

  function syncSettlement(view: PlayerView): void {
    if (view.phase !== 'finished') {
      if (settlement !== null) {
        settlement.destroy({ children: true });
        settlement = null;
        settlementBuilt = false;
      }
      return;
    }
    if (settlementBuilt) return;
    settlementBuilt = true;

    const { width, height } = ctx.bounds;
    const box = new Container();
    box.label = 'settlement';
    box.addChild(scrim({ scale, width, height }).container);

    const W = uiPad(380, scale);
    const pad = uiPad(12, scale);
    const card = panel({ scale, width: W, title: t('common.gameOver') });
    let y = 0;

    const winner =
      view.winner === null ? undefined : view.players.find((p) => p.id === view.winner);
    const headline = richLabel({
      text:
        winner === undefined
          ? t('game.settlement.noWinner')
          : t('game.settlement.winner', { name: winner.name }),
      scale,
      size: 22,
      fill: winner === undefined ? INK.textDim : INK.text,
      role: 'cjk',
      bold: true,
    });
    card.body.addChild(headline);
    y += headline.height + uiPad(14, scale);

    for (const player of view.players) {
      const row = richLabel({
        text: t('game.settlement.row', {
          status: t(player.alive ? 'game.settlement.alive' : 'game.settlement.eliminated'),
          name: player.name,
          n: player.handCount,
        }),
        scale,
        size: 13,
        fill: player.alive ? INK.text : INK.textDim,
        role: 'cjk',
      });
      row.position.set(0, y);
      card.body.addChild(row);
      y += row.height + uiPad(6, scale);
    }
    y += uiPad(10, scale);

    const back = stampButton({
      label: t('common.backPlain'),
      scale,
      variant: 'primary',
      width: uiPad(160, scale),
      height: uiPad(40, scale),
      onPress: options.onLeave,
    });
    back.container.position.set((W - pad * 2 - back.container.width) / 2, y);
    card.body.addChild(back.container);
    y += back.container.height;

    card.fitContent(y);
    card.container.position.set(
      Math.round((width - W) / 2),
      Math.round((height - card.height) / 2),
    );
    box.addChild(card.container);
    settlement = box;
    overlay.addChild(box);
  }

  // ---- 出牌选择：目标（玩家名）/ 牌名（候选牌面）—— 都在**画面中央** ----
  let chooser: Container | null = null;
  let chooserNameButtons: ReturnType<typeof stampButton>[] = [];
  /** 合并面板（目标 + 牌名）里已经点好的目标；只选目标时用不到 */
  let chooserPickedTarget: PlayerId | null = null;

  function closeChooser(): void {
    chooser?.destroy({ children: true });
    chooser = null;
    chooserHeaders = [];
    chooserNameButtons = [];
    chooserPickedTarget = null;
  }

  /**
   * **手动选目标 / 牌名，候选都铺在画面中央。**
   *
   * 用户报：打出一对这类需要目标时，只在左上角列表里显示「选他」；希望像
   * 三张同名那样把**候选玩家的名字飞到画面中央**。于是这里统一成一块居中面板：
   *
   * - `titles === null`：只有候选玩家名，点一个就走；
   * - `titles !== null`（三张同名）：名字 + 待选牌类**同时**铺在中央，
   *   先点名字（选中的收实心、其余变灰），再点一张牌 = 决定牌名并收尾。
   *
   * ⚠️ 以前三张同名是「点队列里的人 → 等着弹出牌名面板」，用户报过牌名面板
   *    不出现导致卡死；两块合进同一个面板后，不存在「第二步没弹出来」的状态。
   */
  function choosePlay(spec: PlayChoiceSpec): void {
    closeChooser();
    const { width, height } = ctx.bounds;
    const box = new Container();
    box.label = 'play-chooser';
    box.addChild(scrim({ scale, width, height, onDismiss: closeChooser }).container);

    const needsTitle = spec.titles !== null;
    const title = richLabel({
      text: t(needsTitle ? 'table.chooseTargetAndName' : 'table.chooseTarget'),
      scale,
      size: 15,
      fill: INK.text,
      role: 'cjk',
      bold: true,
    });
    box.addChild(title);

    // ---- 候选玩家名 ----
    const names = new Container();
    let nx = 0;
    for (const target of spec.targets) {
      const button = stampButton({
        label: target.name,
        scale,
        variant: 'primary',
        height: uiPad(40, scale),
        onPress: () => {
          // 只选目标：点一下直接收尾
          if (!needsTitle) {
            const submit = spec.onPick;
            closeChooser();
            submit(target.id, null);
            return;
          }
          // 目标 + 牌名：先记下目标、把牌面点亮，等玩家再点一张牌
          chooserPickedTarget = target.id;
          for (const other of chooserNameButtons) {
            other.setVariant(other === button ? 'primary' : 'secondary');
          }
          if (cards !== null) cards.alpha = 1;
        },
      });
      button.container.position.set(nx, 0);
      names.addChild(button.container);
      chooserNameButtons.push(button);
      nx += button.width + uiPad(12, scale);
    }
    names.position.set(
      Math.round((width - names.width) / 2),
      Math.round(height / 2 - names.height - uiPad(30, scale)),
    );
    box.addChild(names);

    // ---- 候选牌类型（三张同名才需要）----
    let cards: Container | null = null;
    if (needsTitle) {
      const face = faceSize(uiPad(84, scale));
      const gap = uiPad(10, scale);
      const row = new Container();
      let x = 0;
      for (const t of spec.titles) {
        const def = options.defOf(t);
        if (def === undefined) continue;
        // TrackerHeader = 一张牌面 + hover + 点击，正好复用
        const header = new TrackerHeader({
          faces: ctx.faces,
          def,
          width: face.width,
          height: face.height,
          onPick: () => {
            if (chooserPickedTarget === null) return;
            const submit = spec.onPick;
            const picked = chooserPickedTarget;
            closeChooser();
            submit(picked, t);
          },
        });
        header.view.position.set(x, 0);
        row.addChild(header.view);
        // ★ 牌面下面再印一行**牌名**：候选牌类既看得见图，也读得出字
        const label = richLabel({
          text: cardName(def),
          scale,
          size: 11,
          fill: INK.textDim,
          role: 'cjk',
        });
        label.position.set(x + Math.round((face.width - label.width) / 2), face.height + uiPad(4, scale));
        row.addChild(label);
        chooserHeaders.push(header);
        x += face.width + gap;
      }
      row.position.set(
        Math.round((width - row.width) / 2),
        Math.round(names.y + names.height + uiPad(18, scale)),
      );
      // 没点目标之前牌面是灰的（点上去不生效，见上面的守卫）
      row.alpha = 0.32;
      box.addChild(row);
      cards = row;
    }

    // 标题压在名字/牌面上方
    title.position.set(
      Math.round((width - title.width) / 2),
      Math.round(names.y - title.height - uiPad(14, scale)),
    );
    chooser = box;
    overlay.addChild(box);
  }

  /**
   * **取牌扇**：只有"我在抽牌"时灌数据，其余时候渲染为空。
   *
   * ★ 节点常驻（`pickFan` 一开始就建好），不再是"抽牌才 new 一个"——
   *   和下屏顶部的旁观扇**复用同一个 `SpectatorFan`**。
   */
  function syncFan(view: PlayerView): void {
    const pending = view.pending;
    const mine =
      pending !== null && pending.player === view.you && pending.kind === 'stealCard';
    if (!mine) {
      if (fanActive) {
        pickFan.setEmpty();
        fanActive = false;
        fanCount = -1;
        // ★ 节点先留着：暗底 / 提示从这里开始急停渐隐（见 updateFrame）
        fanChrome.to(0, DURATION.slow);
        // ★ 告诉对面“我不再指任何一张了”。牌扇是被 setEmpty 直接清掉的，
        //   不会触发 pointerout，不发这一条的话被抽的人那张牌会一直抬着。
        options.onFanHover?.(null);
      }
      return;
    }
    const count = pending.steal?.count ?? 0;
    if (!fanActive || fanCount !== count) {
      pickFan.setPick({ count, onHover: options.onFanHover, onPick: options.onPickBack });
      fanActive = true;
      fanCount = count;
      fanChrome.to(1, DURATION.slow);
    }
    // ★ 记牌器开着时整扇不吃指针（它是模态窗口）
    pickFan.setInteractive(!trackerOpen);
    // ★ fanActive 可能刚翻成 true —— 立刻补画暗底/提示（不等下一次视图变化）
    paintFanChrome();
  }

  // ---- 塞回异常：拖拽松手时，把落点换算成那一个合法动作 ----
  draw.setOnInsert((raw) => {
    const count = insertSpots.length;
    if (count === 0) return;
    const at = Math.max(0, Math.min(count - 1, raw));
    const spot = insertSpots[at];
    if (spot !== undefined) options.onAction(spot.action);
  });

  /** 结束回合（和右下角那个按钮同一件事）—— 手柄/程序化也走它 */
  const endTurn = (): void => {
    if (finishAction !== null) options.onAction(finishAction.action);
  };

  /** 打开记牌器（点抽牌堆 = 只开不收；关闭走关闭键/遮罩/Esc） */
  const openTracker = (): void => {
    if (trackerOpen) return;
    trackerOpen = true;
    trackerEnter = 0;
    draw.setExpanded(true);
    layout();
    if (lastView !== null) rebuildTracker(lastView);
  };

  const toggleTracker = (): void => {
    if (trackerOpen) closeTracker();
    else openTracker();
  };

  const readoutOf = (button: typeof finish): {
    label: string;
    enabled: boolean;
    confirm: boolean;
    hold: number;
    hinting: boolean;
    accent: number | null;
  } => ({
    label: button.label,
    enabled: button.enabled,
    confirm: button.confirm,
    hold: Math.round(button.hold * 1000) / 1000,
    hinting: button.hinting,
    accent: button.accent,
  });

  return {
    container,
    overlay,
    fanLayer,
    menuOpen: () => menu.visible,
    closeMenu: () => menu.close(),
    buttons: () => ({ finish: readoutOf(finish) }),
    actionTip: () => {
      // 鼠标走 hover；触控按住看说明（`hinting`），两者共用同一块面板
      if (finishTip === null || !finish.enabled || !(finish.hovered || finish.hinting)) return null;
      return {
        title: finish.label,
        tip: finishTip,
        ...(finishTipParams === undefined ? {} : { tipParams: finishTipParams }),
        box: {
          x: finish.container.position.x,
          y: finish.container.position.y,
          width: finish.width,
          height: finish.height,
        },
      };
    },
    setTargetHighlight(target: PlayerId | null): void {
      pinnedTarget = target;
    },

    update(view, intent): void {
      scale = ctx.settings.get().uiScale;
      lastView = view;
      layout();

      // ---- 固定件 ----
      queue.setPlayers(view.players, view.turn?.player ?? view.pending?.player ?? null);
      queue.onPickTarget = options.onPickTarget;
      queue.setTargets(intent.targets);
      // ★ 抽牌堆按「已知 / 未知」分段画：自己确定的牌画牌面，其余画牌背
      draw.setPile(drawPileFaces(view));
      drawCount.text = t('game.cardCount', { n: view.drawPileCount });

      // ★ 被要求给牌的人：屏幕中央给一句提示（D2）
      const giving =
        view.pending !== null && view.pending.player === view.you && view.pending.kind === 'giveCard';
      prompt.visible = giving;
      if (giving) {
        const named = view.pending?.namedTitle ?? null;
        const namedDef = named === null ? undefined : ctx.registry.get(named);
        promptText.text =
          namedDef === undefined
            ? t('game.give.unnamed')
            : t('game.give.named', { name: cardName(namedDef) });
        prompt.position.set(
          Math.round(ctx.bounds.width / 2 - prompt.width / 2),
          Math.round(ctx.bounds.height / 2 + uiPad(90, scale)),
        );
      }

      // ---- 右下角那一个按钮 ----
      finishAction = intent.finish;
      finishTip = intent.finish?.tip ?? null;
      finishTipParams = intent.finish?.tipParams;
      // ★ 濒死（`confirm`）：变红 + 长按。两个动作是同一个开关，见 `setConfirm`
      finish.setConfirm(intent.confirm);
      if (finishAction === null) {
        finish.setEnabled(false);
        finish.setAccent(null);
      } else {
        // ★ 被攻击的第一个额外回合：按**来源牌**的辨识色染结束键。
        //   颜色从卡牌定义/牌面画法里读（`cardAccent`），界面不写死任何颜色。
        const attack = finishAction.attack;
        const source = attack === null ? undefined : ctx.registry.get(attack.source);
        finish.setAccent(source === undefined ? null : cardAccent(source));
        finish.setLabel(finishAction.label);
        finish.setEnabled(true);
      }

      // ---- 牌堆上的"塞回异常" ----
      insertSpots = intent.insert;
      // ★ 塞回异常：自动展开 + 逐张画；把 [异常] 作为堆顶一张一起摆出来，
      //   然后**强制进入拖拽态** —— 牌没有被按着也跟指针走，复用
      //   CardStrip 的 override 手感（用户要求：别自己算点哪儿）。
      const inserting = insertSpots.length > 0;
      const lethalTitle =
        view.pending?.kind === 'insertLethal' ? view.pending.revealedTitle : null;
      draw.setInsertLethal(inserting ? lethalTitle : null);
      draw.setInsertMode(inserting);
      if (inserting) {
        draw.setExpanded(true);
        layout();
        if (!draw.insertDragging) draw.beginInsertDrag();
      } else {
        draw.cancelInsert();
        if (!trackerOpen) draw.setExpanded(false);
      }
      // 抽牌堆**始终可点**：非插入模式下点它就是开关记牌器
      draw.setPickable(true);
      draw.setOnPick(() => {
        // ★ 只**打开**、不收起：移动端不方便 hover，点一下不应把面板关掉
        //   （关闭走关闭键 / 点遮罩 / Esc）
        openTracker();
      });
      // ★ 记牌器取代了弃牌堆：要"从弃牌堆捞一张"时自动打开它，表头可点选牌型
      pickMap = intent.pick;
      if (pickMap.size > 0 && !trackerOpen) {
        trackerOpen = true;
        trackerEnter = 0;
        draw.setExpanded(true);
        layout();
      }

      syncFan(view);
      syncSettlement(view);
      if (trackerOpen) rebuildTracker(view);
    },

    updateFrame(deltaMS): void {
      // ★ 目标高亮：hover 的优先，其次是被选定的那个；每帧推一次 150ms 补间
      targetEdge.setActive((hoverTarget ?? pinnedTarget) !== null);
      targetEdge.update(deltaMS);
      queue.update(deltaMS);
      // ★ 抽牌堆每帧推进（间距展开 / hover 抬起都是它自己在缓动）
      draw.update(deltaMS);
      // ★ 取牌扇每帧推进 —— 不推的话所有牌都停在原点，叠成一摞不成扇
      pickFan.updateFrame(deltaMS);
      // ★ 记牌器表头 / A2 选牌的小牌 idle（现在由 CardActor 的 CardRig 驱动）
      for (const head of trackerHeads) {
        if (!head.view.destroyed) head.update(deltaMS);
      }
      for (const head of chooserHeaders) {
        if (!head.view.destroyed) head.update(deltaMS);
      }
      // ★ 抽牌堆：常态右上角 / 展开时屏幕中心上方。
      //   目标**每帧**按当前内容宽度算；位置按 `EXPAND_MS` 定长走 ease-out。
      const s = scale;
      const { width, height } = ctx.bounds;
      // ★ 塞回异常时牌堆**居中**（视口本来就占满整屏宽），玩家用横轴选插入位；
      //   不居中而沿用右上角的话，展开的序列会有一大半落在屏幕外。
      const insertingNow = insertSpots.length > 0;
      const target = trackerOpen
        ? { x: width / 2, y: height / 2 - uiPad(180, s) }
        : insertingNow
          ? { x: width / 2, y: height / 2 }
          : {
              x: uiPad(width - RIGHT_MARGIN, s) - draw.contentWidth / 2,
              y: uiPad(DRAW_Y, s) + draw.size.height / 2,
            };
      if (!drawInit) {
        drawPos = { ...target };
        drawAnimFrom = { ...target };
        drawLastTarget = { ...target };
        drawInit = true;
      } else if (
        drawLastTarget === null ||
        Math.abs(target.x - drawLastTarget.x) > 0.5 ||
        Math.abs(target.y - drawLastTarget.y) > 0.5
      ) {
        // 目标变了（收起 ↔ 展开，或牌数变了）→ 重新起一段定长动画
        drawAnimFrom = { ...drawPos };
        drawAnimElapsed = 0;
        drawLastTarget = { ...target };
      }
      drawAnimElapsed = Math.min(drawAnimElapsed + deltaMS, EXPAND_MS);
      const progress = EXPAND_MS <= 0 ? 1 : drawAnimElapsed / EXPAND_MS;
      const eased = 1 - (1 - progress) ** 3;
      drawPos = {
        x: drawAnimFrom.x + (target.x - drawAnimFrom.x) * eased,
        y: drawAnimFrom.y + (target.y - drawAnimFrom.y) * eased,
      };
      draw.view.position.set(drawPos.x, drawPos.y);
      drawTop = { x: drawPos.x, y: drawPos.y };
      updateDrawCount();

      // ★ 塞回异常提示：每帧跟着被拖那张 [异常] 摆位置，并按当前落点刷新
      //   “第几张被抽到 / 无人洗牌时谁抽到”。和松手提交用同一套换算，绝不会对不上。
      if (draw.insertDragging && lastView !== null && insertSpots.length > 0) {
        const raw = draw.insertIndexNow();
        const at = Math.max(0, Math.min(insertSpots.length - 1, raw));
        const box = draw.insertBox();
        if (raw >= 0 && box !== null) {
          const outcome = insertOutcome(lastView, at);
          insertHint.show(box, outcome.drawsUntil, outcome.drawerName);
        } else {
          insertHint.hide();
        }
      } else {
        insertHint.hide();
      }

      // ★ 取牌暗底 / 提示：每帧推一下透明度进度 —— 行动完成后急停渐隐。
      //   节点不在这时候销毁，否则 alpha 还没走到 0 就整块没了。
      if (fanChrome.moving) {
        fanChrome.advance(deltaMS);
        applyFanChromeAlpha();
      }

      // ★ 记牌器窗口 / 背景的进入动画：和抽牌堆飞行同一条时间线（都是 `EXPAND_MS`）
      if (trackerOpen && trackerEnter < 1) {
        trackerEnter = Math.min(1, trackerEnter + deltaMS / EXPAND_MS);
        if (tracker !== null) tracker.alpha = trackerEnter;
      }
    },

    center() {
      return { x: uiPad(FAN_CENTER.x, scale), y: uiPad(FAN_CENTER.y, scale) };
    },

    drawTop() {
      return drawTop;
    },

    playOrigin() {
      const { width, height } = ctx.bounds;
      return { x: width / 2, y: uiPad(height - 120, scale) };
    },

    handTop: 660,

    fan() {
      return fanActive ? pickFan : null;
    },

    choosePlay,
    chooserOpen: () => chooser !== null,
    closeChooser,

    trackerOpen: () => trackerOpen,
    closeTracker,
    endTurn,
    toggleTracker,
    trackerHover() {
      const hovered = trackerHeadHover;
      if (hovered !== null) {
        return {
          hoveredDef: hovered.def,
          // 表头在遮罩 + 横向滚动的网格里，getBounds() 是世界坐标 —— 换算回逻辑坐标
          hoveredBox: () => toLocalBox((point) => overlay.toLocal(point), hovered.view.getBounds()),
        };
      }
      // 其次是抽牌堆里 hover 到的（已知）牌
      return draw.hoverSource();
    },
    trackerCellTip() {
      const hover = trackerCellHover;
      if (hover === null) return null;
      return {
        title: hover.title,
        tip: 'game.tracker.cellExact',
        tipParams: {
          value: Number.isInteger(hover.value) ? String(hover.value) : hover.value.toFixed(2),
        },
        // 数字格在遮罩 + 横向滚动的网格里，getBounds() 是世界坐标 —— 换算回逻辑坐标
        box: toLocalBox((point) => overlay.toLocal(point), hover.view.getBounds()),
      };
    },

    destroy(): void {
      closeChooser();
      closeTracker();
      pickFan.destroy();
      fanGradient.destroy();
      fanLayer.destroy();
      queue.destroy();
      draw.destroy();
      insertHint.destroy();
      targetEdge.destroy();
      menu.destroy();
      container.destroy({ children: true });
    },
  };
}

// ============================================================
// 齿轮 + 它的小菜单
// ============================================================

interface GearMenuOptions {
  readonly scale: number;
  readonly canvas: { readonly width: number; readonly height: number };
  readonly onSettings: () => void;
  readonly onLeave: () => void;
  /** 联机才有：刷新同步。不传就不显示这个按钮 */
  readonly onSync?: () => Promise<void> | void;
}

interface GearMenu {
  readonly button: Container;
  /** ★ 全屏那一层（遮罩 + 居中面板）。**由调用方加在最后** —— 见 `TableChrome.overlay` */
  readonly overlay: Container;
  open(): void;
  close(): void;
  readonly visible: boolean;
  layout(scale: number): void;
  destroy(): void;
}

/**
 * **齿轮按钮 + 它那一层全屏菜单。**
 *
 * 评审：「设置改为一个齿轮图标放在右下角」+「设置按钮不会直接打开设置页，
 * 而是打开菜单，包含"设置"，"保存并退出"（序列化先搁置）等一系列选项」，
 * 以及「按下设置按钮后弹出的菜单应当是**盖在整个屏幕之上**的，而不是在底下
 * 那角落里」。
 *
 * 所以菜单不是"齿轮旁边的一小块"，而是：
 *
 * ```
 *   全屏遮罩（压暗整屏 + 吃掉所有点击）  ← 这就是"盖在整个屏幕之上"
 *     └── 居中的面板（设置 / 保存并退出 / 退出对局）
 * ```
 *
 * ⚠️ 遮罩必须 `eventMode = 'static'` 且铺满：不然点"面板外面"会把点击漏到
 * 底下的牌桌上（`screenHost.ts` 的遮罩用的是同一个道理）。
 *
 * ⚠️ 齿轮是**画出来的**（`Graphics`：一圈轮 + 8 颗牙），不是字符 `⚙` ——
 * 这套设计语言里没有 emoji / 图标字体（`docs/ui-design.md`）。
 */
function createGearMenu(options: GearMenuOptions): GearMenu {
  const s = options.scale;
  const size = uiPad(GEAR_SIZE, s);
  const button = new Container();
  button.label = 'gear-button';
  const background = new Graphics();
  const cog = new Graphics();
  button.addChild(background, cog);

  // ---- 全屏那一层 ----
  const overlay = new Container();
  overlay.label = 'table-menu';
  overlay.visible = false;
  // ⚠️ 遮罩走**组件库**（`render/ui/scrim.ts`）：界面层不许自己挂指针事件
  const backdrop = scrim({
    scale: s,
    width: options.canvas.width,
    height: options.canvas.height,
    onDismiss: () => {
      overlay.visible = false;
    },
  });
  overlay.addChild(backdrop.container);

  const box = panel({ scale: s, width: MENU_W, title: t('game.menu.title') });
  overlay.addChild(box.container);
  const column = createColumn(s, 8);

  const close = (): void => {
    overlay.visible = false;
  };

  /**
   * **手动同步（联机才有）。** 反馈直接写在按钮文字上：同步中… → 已同步 /
   * 同步失败 →（2 秒后）刷新同步。不关菜单 —— 玩家要看得见结果。
   */
  let syncResetTimer: ReturnType<typeof setTimeout> | null = null;
  if (options.onSync !== undefined) {
    const runSync = options.onSync;
    const syncButton = stampButton({
      label: t('game.menu.sync'),
      scale: s,
      variant: 'secondary',
      width: MENU_W - 24,
      onPress: () => {
        if (syncResetTimer !== null) {
          clearTimeout(syncResetTimer);
          syncResetTimer = null;
        }
        syncButton.setEnabled(false);
        syncButton.setLabel(t('game.menu.syncing'));
        void (async () => {
          try {
            await runSync();
            syncButton.setLabel(t('game.menu.synced'));
          } catch (error) {
            console.warn(t('game.online.syncFailed'), error);
            syncButton.setLabel(t('game.menu.syncFailed'));
          } finally {
            syncButton.setEnabled(true);
            syncResetTimer = setTimeout(() => {
              syncResetTimer = null;
              syncButton.setLabel(t('game.menu.sync'));
            }, 2000);
          }
        })();
      },
    });
    column.add(syncButton);
  }

  column.add(
    stampButton({
      label: t('common.settings'),
      scale: s,
      variant: 'secondary',
      width: MENU_W - 24,
      onPress: () => {
        close();
        options.onSettings();
      },
    }),
  );

  // ⚠️ 「保存并退出」的**保存还没做**（棋谱序列化在 STATUS 里搁着），
  //    所以它是禁用的（撤墨 + 注销杠），不是"点了没反应"。
  //    ⚠️ 不再附一行说明：评审要求把界面上那些解释性小字一并删掉
  column.add(
    stampButton({
      label: t('game.menu.saveAndExit'),
      scale: s,
      variant: 'secondary',
      width: MENU_W - 24,
      enabled: false,
      onPress: () => undefined,
    }),
  );

  column.add(
    stampButton({
      label: t('game.menu.leave'),
      scale: s,
      variant: 'quiet',
      width: MENU_W - 24,
      onPress: () => {
        close();
        options.onLeave();
      },
    }),
  );
  // ★ 「返回」= 只关菜单，什么都不做。
  //   以前没有它，要出菜单只能点面板外面或按 Esc —— 用户报「找不到关闭菜单的按钮」。
  column.add(
    stampButton({
      label: t('common.back'),
      scale: s,
      variant: 'secondary',
      width: MENU_W - 24,
      onPress: () => {
        close();
      },
    }),
  );

  // ⚠️ 内容要进 `body`（它已经让开标题栏和内边距）；加到 `container` 会顶到
  //    标题栏和边框上 —— 这正是齿轮菜单"对不齐"的根因。
  box.body.addChild(column.container);
  box.fitContent(Math.round(column.y));

  function layout(_scale: number): void {
    const { width, height } = options.canvas;
    backdrop.resize(width, height);
    box.container.position.set(
      Math.round((width - box.container.width) / 2),
      Math.round((height - box.container.height) / 2),
    );
  }

  const surface = createSurface({
    container: button,
    width: size,
    height: size,
    onPress: () => {
      overlay.visible = !overlay.visible;
    },
    onStateChange: (state, motion) => paint(state, motion.mix, motion.lift),
    onFrame: (state, motion) => paint(state, motion.mix, motion.lift),
  });

  function paint(state: string, mix: number, lift: number): void {
    const ink = stampInkAt('quiet', state as Parameters<typeof stampInkAt>[1], mix);
    background.clear();
    background
      .rect(0, lift, size, size)
      .fill({ color: ink.fill })
      .stroke({ color: ink.edge, width: uiLine(2, s), alignment: 0 });

    const cx = size / 2;
    const cy = size / 2 + lift;
    const rInner = size * 0.19;
    const rOuter = size * 0.31;
    const tooth = uiLine(3, s);
    cog.clear();
    cog.circle(cx, cy, rInner).stroke({ color: ink.label, width: tooth });
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      cog
        .moveTo(cx + Math.cos(angle) * rInner, cy + Math.sin(angle) * rInner)
        .lineTo(cx + Math.cos(angle) * rOuter, cy + Math.sin(angle) * rOuter);
    }
    cog.stroke({ color: ink.label, width: tooth });
  }

  // ⚠️ 首帧必须自己画一次（`createSurface` 只在状态**变化**时回调）——
  //    漏掉的话齿轮在闲置时是**一块空白**，鼠标划过去才出现（`button.ts` 同一个坑）
  paint('idle', surface.motion.mix, surface.motion.lift);
  layout(s);

  return {
    button,
    overlay,
    open() {
      overlay.visible = true;
    },
    close,
    get visible() {
      return overlay.visible;
    },
    layout,
    destroy() {
      if (syncResetTimer !== null) {
        clearTimeout(syncResetTimer);
        syncResetTimer = null;
      }
      box.container.destroy({ children: true });
      overlay.destroy({ children: true });
      button.destroy({ children: true });
    },
  };
}
